import * as React from "react";
import styled from "@emotion/styled";
import Form from "react-bootstrap/Form";
import { easelColors, designTokens } from "../../helpers/constants";
import { mq } from "../../helpers/mediaQueries";
import { usePublishModal } from "./usePublishModal";
import { EmptyState, EmptyStateType } from "./EmptyState";

export interface ExtraItem {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
}

interface FormattedResource {
  title: string;
  id: string;
  thumbnail?: string;
}

interface ResourcesProps {
  scrollTarget: React.MutableRefObject<HTMLDivElement | null>;
  resources: (FormattedResource | null)[];
  totalCount: number | null;
  currentCount: number | null;
  extraItems?: Array<ExtraItem>;
}

export const Resources: React.FC<ResourcesProps> = (props: ResourcesProps) => {
  const { scrollTarget, resources, totalCount, currentCount, extraItems } =
    props;
  const { selectedResource, setSelectedResource } = usePublishModal();

  const emptyState: EmptyStateType | null = React.useMemo(() => {
    return totalCount === 0
      ? EmptyStateType.NoResources
      : currentCount === 0
      ? EmptyStateType.NoCurrentResources
      : null;
  }, [totalCount, currentCount]);

  return (
    <ResourceWrapper>
      {emptyState === null || (extraItems && extraItems.length > 0) ? (
        <>
          {extraItems?.map((item, key) => (
            <Resource
              data-testid="extra-item-label"
              key={key.toString()}
              onClick={item.onClick}
            >
              <IconWrapper>
                <IconAlign>{item.icon}</IconAlign>
              </IconWrapper>
              <Title>{item.title}</Title>
            </Resource>
          ))}
          {resources.map((resource) => {
            if (resource) {
              const isSelected = selectedResource?.id === resource.id;
              return (
                <Resource
                  data-testid="resource-label"
                  selected={isSelected}
                  key={resource.id}
                >
                  <RadioButton
                    checked={isSelected}
                    onChange={() => setSelectedResource(resource)}
                  />
                  <Image src={resource.thumbnail} alt="thumb" />
                  <Title>{resource.title}</Title>
                </Resource>
              );
            }
            return null;
          })}
        </>
      ) : (
        <EmptyState type={emptyState} />
      )}
      <ScrollTarget ref={scrollTarget} />
    </ResourceWrapper>
  );
};

const ResourceWrapper = styled.div({
  display: "flex",
  flexWrap: "wrap",
  height: "50vh",
  alignContent: "flex-start",
});

const Resource = styled.label((props: { selected?: boolean }) =>
  mq({
    flexBasis: ["calc(100% - 1rem)", null, "calc(50% - 1rem)"],
    margin: "0.5rem",
    display: "flex",
    alignItems: "center",
    padding: "1rem",
    borderRadius: "0.5rem",
    minHeight: "5.625rem",
    minWidth: "20rem",
    cursor: "pointer",
    border: `1px solid ${
      props.selected ? "transparent" : designTokens["ds-color-gray-lighter"]
    }`,
    boxShadow: props.selected
      ? `0px 0px 0px 3px ${easelColors.easelPeriwinkle400}`
      : "unset",
    background: easelColors.white,
  })
);

const Image = styled.img({
  height: "4.375rem",
  width: "4rem",
  flexBasis: "20%",
  flexShrink: 0,
  marginRight: "1rem",
  objectFit: "contain",
});

const Title = styled.span({
  fontSize: "0.875rem",
  flexBasis: "60%",
  flexShrink: 0,
});

const IconWrapper = styled.div({
  flexBasis: "10%",
  flexShrink: 0,
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 0.25rem",
});

const IconAlign = styled.div({
  width: "1rem",
  direction: "rtl",
  paddingRight: "12px",
});

const RadioButton = styled(Form.Check)({
  flexBasis: "10%",
  flexShrink: 0,
  paddingLeft: "0",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  margin: "0 0.25rem",

  ".form-check-input": {
    height: "1.5rem",
    width: "1.5rem",
    cursor: "pointer",
    appearance: "none",
    borderRadius: "50%",
    backgroundPosition: "center",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    border: "1px solid rgba(0, 0, 0, 0.25)",
    verticalAlign: "top",
    position: "unset",
  },
  ".form-check-input:focus": {
    boxShadow: "none",
    borderColor: "rgba(0, 0, 0, 0.25)",
    outline: "none",
  },

  ".form-check-input:checked": {
    backgroundColor: easelColors.white,
    border: `6px solid ${easelColors.easelPeriwinkle400}`,
    backgroundImage: "none",
  },
});

const ScrollTarget = styled.div({
  marginTop: "3rem",
  width: "100%",
  height: "5px",
});
